import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { ChatNotificationProvider } from '../../contexts/ChatNotificationContext';
import Sidebar from '../Sidebar/Sidebar';
import TopNavbar from '../TopNavbar/TopNavbar';
import ChatFlutuante from '../ChatFlutuante/ChatFlutuante';
import ChamadoFloatButton from '../ChamadoFloatButton/ChamadoFloatButton';
import styles from './Layout.module.css';
import { tocarSomPedido, prepararSomPedido } from '../../utils/somPedidoCatalogo';

const brlPedido = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const Layout = ({ children }) => {
  // Mobile: drawer começa fechado. Desktop: sidebar começa aberta
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => !isMobile());
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Pedido novo do catálogo (Catálogo Cliente): som + aviso clicável no canto (fica até fechar ou 30s).
  const [pedidosNovos, setPedidosNovos] = useState([]);
  useEffect(() => prepararSomPedido(), []);
  const abrirPedidos = () => {
    setPedidosNovos([]);
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
    navigate('/catalogo-cliente?aba=pedidos');
  };

  // Fecha sidebar ao redimensionar pra mobile, mantém aberto no desktop
  useEffect(() => {
    const onResize = () => {
      if (isMobile()) setIsSidebarOpen(false);
      else setIsSidebarOpen(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fecha sidebar automaticamente no mobile ao navegar de rota
  const location = useLocation();
  useEffect(() => {
    if (isMobile()) setIsSidebarOpen(false);
  }, [location.pathname]);

  // Conexão Global de Socket para Presença Online
  useEffect(() => {
    if (!user?.id) return; // Só conecta se houver usuário logado

    // Usa a URL do backend (variável de ambiente ou assume a mesma origem se em prod)
    // Em desenvolvimento local, precisa ser a porta do backend (3001)
    const socketUrl = import.meta.env.VITE_API_URL || window.location.origin; // Em dev, VITE_API_URL costuma ser http://localhost:3001

    console.log('🔌 Conectando ao Socket.IO Global para monitoramento...');
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'], // Fallback para polling se websocket falhar
      reconnection: true
    });

    // Quando conectar, entrar no room específico do usuário
    socket.on('connect', () => {
      console.log(`🔌 Socket conectado! Entrando no room user_${user.id}`);
      socket.emit('join_user_room', { userId: user.id });
    });

    // Ouvir novas notificações em tempo real (broadcast geral)
    socket.on('new_notification', (data) => {
      console.log('🔔 Nova notificação recebida via Socket!', data);
      // Verificar se a notificação é para este usuário
      const isForMe = !data.usuario_responsavel_id || data.usuario_responsavel_id === user.id;
      if (isForMe) {
        queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
      }
    });

    // Pedido novo no catálogo da Centro (o servidor só manda para administradores)
    socket.on('catalogo_pedido_novo', (p) => {
      tocarSomPedido();
      queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
      setPedidosNovos((lista) => [{ ...p, chegou: Date.now() }, ...lista.filter((x) => x.id !== p.id)].slice(0, 3));
      setTimeout(() => setPedidosNovos((lista) => lista.filter((x) => x.id !== p.id)), 30000);
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
          const n = new Notification(`Novo pedido #${p.codigo || p.id} no catálogo`, { body: `${p.pecas} peça(s) · ${brlPedido(p.valor)}` });
          n.onclick = () => { window.focus(); n.close(); };
        }
      } catch { /* sem notificação do sistema */ }
    });

    // Ouvir notificações direcionadas especificamente para este usuário
    socket.on('user_notification', (data) => {
      console.log('🔔 Notificação DIRECIONADA recebida via Socket!', data);
      queryClient.invalidateQueries({ queryKey: ['user-notifications'] });
    });

    return () => {
      socket.disconnect();
    };
  }, [queryClient, user?.id]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <ChatNotificationProvider>
      <div className={styles.layout}>
        <TopNavbar toggleSidebar={toggleSidebar} />
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
        <main className={`${styles.main} ${isSidebarOpen ? styles.mainShifted : ''}`}>
          <div className={styles.content}>
            {children}
          </div>
        </main>
        {/* Chat flutuante — disponível em todas as páginas exceto /chat */}
        <ChatFlutuante />
        {/* Botão flutuante de chamados */}
        <ChamadoFloatButton />
        {pedidosNovos.length > 0 && (
          <div style={{ position: 'fixed', right: 20, bottom: 96, zIndex: 3000, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pedidosNovos.map((p) => (
              <div key={p.id} role="alert" onClick={abrirPedidos}
                style={{ background: '#15803d', color: '#fff', borderRadius: 12, padding: '12px 40px 12px 16px', boxShadow: '0 8px 24px rgba(0,0,0,.25)', cursor: 'pointer', minWidth: 260, position: 'relative', fontSize: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>🛒 Novo pedido #{p.codigo || p.id}</div>
                <div>{p.pecas} peça(s) · {brlPedido(p.valor)}</div>
                <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>Clique para abrir os pedidos</div>
                <button type="button" aria-label="Fechar" onClick={(e) => { e.stopPropagation(); setPedidosNovos((l) => l.filter((x) => x.id !== p.id)); }}
                  style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 0, color: '#fff', fontSize: 18, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </ChatNotificationProvider>
  );
};

export default Layout;
